import type { StandardDetail, Substandard } from '@/standards/standard_types'
import type { ListMarkerType } from '@/standards/list_markers'
import type { ListItem, StandardBlock, StandardTerm, TextStyle } from '@/standards/standard_blocks'
import { forSetCodes } from '@/standards/set_code_map'
import { fieldText, hasField } from '@/standards/standard_fields'
import { eldStandardLevelLabel, panelHeaderTextForStandard } from '@/standards/standard_panel_header_text'

type LayoutInput = {
  standard: StandardDetail
  field: (name: string) => string
  has: (name: string) => boolean
  standardText: string
}

type Layout = (input: LayoutInput) => Array<StandardBlock | undefined>

type SubstandardOptions = {
  bulletField?: 'substandard' | 'substandard_let'
  olType?: ListMarkerType
  isFullListShown?: boolean
}

const WIDA_COPYRIGHT =
  'WIDA English Language Development Standards Framework, © 2020 Board of Regents of the University of Wisconsin System wida.wisc.edu'

const heading = (text: string): StandardBlock => ({ kind: 'heading', rank: 'primary', text })
const subheading = (text: string): StandardBlock => ({ kind: 'heading', rank: 'secondary', text })
const text = (value: string, style: TextStyle = 'plain'): StandardBlock => ({ kind: 'text', text: value, style })
const html = (value: string): StandardBlock => ({ kind: 'html', html: value, style: 'block' })
const htmlParagraph = (value: string): StandardBlock => ({ kind: 'html', html: value, style: 'paragraph' })
const indentedHtmlParagraph = (value: string): StandardBlock => ({ kind: 'html', html: value, style: 'indentedParagraph' })
const preLine = (value: string): StandardBlock => ({ kind: 'preLine', text: value })
const terms = (values: StandardTerm[]): StandardBlock => ({ kind: 'terms', terms: values })
const lineBreak: StandardBlock = { kind: 'lineBreak' }

function when(condition: boolean, block: StandardBlock): StandardBlock | undefined {
  if (!condition) {
    return undefined
  }
  return block
}

function joinPresent(parts: string[], separator = ' '): string {
  return parts.filter(Boolean).join(separator)
}

function prefixed(prefix: string, value: string): string {
  if (!prefix) {
    return value
  }
  return `${prefix}: ${value}`
}

function substandards(standard: StandardDetail, options: SubstandardOptions = {}): StandardBlock {
  const { bulletField = 'substandard', olType = 'a', isFullListShown = true } = options
  return {
    kind: 'substandards',
    orderedSubstandards: isFullListShown ? standard.substandards ?? null : null,
    olType,
    singleSubstandardBullet: fieldText(standard, bulletField),
    singleSubstandardText: fieldText(standard, 'substandard_text')
  }
}

function substandardKey(substandard: Substandard, index: number): string {
  return substandard.standard_long_ref || substandard.standard_short_ref || String(index)
}

function substandardItems(standard: StandardDetail): ListItem[] {
  return (standard.substandards ?? []).map((substandard, index) => ({
    key: substandardKey(substandard, index),
    text: substandard.standard_text
  }))
}

function componentList(standard: StandardDetail): StandardBlock | undefined {
  return when(Boolean(standard.substandards), { kind: 'componentList', items: substandardItems(standard) })
}

const headingThenHtml = (headingField: string): Layout => ({ field, standardText }) => [
  heading(field(headingField)),
  html(standardText)
]

const headingThenHtmlParagraph = (headingField: string): Layout => ({ field, standardText }) => [
  heading(field(headingField)),
  htmlParagraph(standardText)
]

const headingHtmlSubstandards = (headingField: string, options?: SubstandardOptions): Layout => ({ standard, field, standardText }) => [
  heading(field(headingField)),
  html(standardText),
  substandards(standard, options)
]

const headingParagraphSubstandards = (headingField: string, options?: SubstandardOptions): Layout => ({ standard, field, standardText }) => [
  heading(field(headingField)),
  htmlParagraph(standardText),
  substandards(standard, options)
]

const headingLineParagraph = (headingField: string, lineField: string): Layout => ({ field, standardText }) => [
  heading(field(headingField)),
  text(field(lineField)),
  htmlParagraph(standardText)
]

const htmlOnly: Layout = ({ standardText }) => [html(standardText)]

const mathSubjectClusterHeading = ({ field }: LayoutInput) => prefixed(field('math_hs_subject'), field('cluster'))

const defaultLayout: Layout = ({ standard, field, standardText }) => [
  heading(field('standard_short_ref')),
  html(standardText),
  substandards(standard)
]

function ncscosStandardText({ field, has, standardText }: LayoutInput): string {
  if (has('math_hs_goal')) {
    return `(${field('math_hs_course')}) ${standardText}`
  }
  if (has('level')) {
    return `(${field('level')}) ${standardText}`
  }
  return standardText
}

function acsLayout(input: LayoutInput): Array<StandardBlock | undefined> {
  const { standard, field, has, standardText } = input
  const subjectCode = field('subject_code')
  if (subjectCode === 'M') {
    const courseAndFocusArea = joinPresent([field('math_hs_course'), field('math_hs_focus_area')], ': ')
    return [
      when(Boolean(courseAndFocusArea), heading(courseAndFocusArea)),
      subheading(field('cluster')),
      html(standardText),
      substandards(standard)
    ]
  }
  if (subjectCode === 'ELA') {
    return [when(has('cluster'), heading(field('cluster'))), text(field('strand')), html(standardText), substandards(standard)]
  }
  return defaultLayout(input)
}

function pcsLayout({ field, has, standardText }: LayoutInput): Array<StandardBlock | undefined> {
  const subjectCode = field('subject_code')
  const clusterDescription = field('cluster_description')
  const elaHeading = has('cluster_text') ? `${clusterDescription}: ${field('cluster_text')}` : clusterDescription
  return [
    when(subjectCode === 'ELA', text(field('strand_domain_text'), 'flush')),
    when(subjectCode === 'ELA', heading(elaHeading)),
    when(subjectCode === 'M', heading(clusterDescription)),
    html(standardText)
  ]
}

const LAYOUT_BY_SET_CODE: Record<string, Layout> = {
  'DLM-EE': headingThenHtml('cc_cluster'),
  'NCSC-CCC': ({ field, standardText }) => [heading(field('strand')), preLine(standardText)],
  'NY-PreK': ({ field, standardText }) => [heading(`${field('standard_short_ref')} ${field('strand')}`), preLine(standardText)],
  'CDC-AEM': ({ field, standardText }) => [heading(`${field('month')} Months: ${field('subject')}`), text(standardText, 'flush')],
  CPLF: ({ standard, field, standardText }) => [
    heading(field('strand')),
    text(`${field('standard_num')} ${field('domain')}`),
    when(!standard.substandards, text(`${field('age')}. ${standardText}`, 'indented')),
    when(Boolean(standard.substandards), { kind: 'indentedList', items: substandardItems(standard) })
  ],
  TPKG: ({ standard, standardText }) => [heading(panelHeaderTextForStandard(standard)), html(standardText), componentList(standard)],
  TPG: headingThenHtml('subject'),
  'CA-ELD': ({ standard, field, standardText }) => [
    heading(panelHeaderTextForStandard(standard)),
    terms([
      { label: `Part ${field('part_code')}`, description: field('part') },
      { label: `Mode ${field('mode_code')}`, description: field('mode') },
      { label: `Level ${field('standard_num')}: ${field('proficiency_level')} (${eldStandardLevelLabel(standard)})` }
    ]),
    preLine(standardText)
  ],
  CCSS: ({ standard, field, standardText }) => [
    heading(`${field('standard_short_ref')} ${field('cluster')}`),
    html(standardText),
    componentList(standard)
  ],
  ...forSetCodes(['CA-PE', 'FL.RES', 'WCSDC'], ({ standardText }) => [text(standardText)]),
  'ILS-SEL': ({ standard, field, standardText }) => [
    heading(panelHeaderTextForStandard(standard)),
    terms([
      { label: `Goal ${field('ils_goal_code')}`, description: field('ils_goal') },
      { label: `Standard ${field('ils_standard_code')}`, description: field('ils_standard') },
      { label: `Grades ${field('grades_str')}` },
      { label: `Benchmark ${field('standard_num')}` }
    ]),
    preLine(standardText)
  ],
  IELDS: ({ standard, field, standardText }) => [
    heading(field('cluster')),
    htmlParagraph(standardText),
    substandards(standard, { bulletField: 'substandard_let' }),
    text(field('footer'), 'indented')
  ],
  TEKS: ({ field, has, standardText }) => [
    heading(field('strand')),
    text(field('strand_description')),
    when(has('standard_num'), preLine(`${field('standard_num')}. ${standardText}`))
  ],
  ETEKS: headingLineParagraph('strand_domain', 'strand_domain_text'),
  'TEKS.PCT': headingParagraphSubstandards('strand_domain', { olType: 'A' }),
  'TEKS-2017': ({ field, has, standardText }) => {
    const romanNumeral = has('standard_roman_numeral') ? `.${field('standard_roman_numeral')}` : ''
    return [preLine(`${field('standard_num')}${field('standard_letter')}${romanNumeral}. ${standardText}`)]
  },
  ...forSetCodes(['TEKS-SCI', 'SC-CCR'], ({ standard, field, has, standardText }) => [
    when(has('hs_course'), heading(field('hs_course'))),
    htmlParagraph(standardText),
    substandards(standard, { olType: 'A' })
  ]),
  'GSE.PE': ({ field, has, standardText }) => [
    when(has('domain'), heading(field('domain'))),
    when(has('hs_course'), text(field('hs_course'))),
    htmlParagraph(standardText)
  ],
  ...forSetCodes(['TEKS.CTE', 'RIELDS', 'TN-ELDS', 'MT-ELS'], headingThenHtml('cluster')),
  ITELG: headingThenHtml('indicator'),
  NCSCoS: (input) => [
    heading(input.has('math_hs_goal') ? input.field('math_hs_goal') : input.field('cluster')),
    html(ncscosStandardText(input)),
    substandards(input.standard, { bulletField: 'substandard_let' })
  ],
  HELDS: ({ field, standardText }) => [
    heading(field('strand')),
    text(field('topic')),
    htmlParagraph(standardText),
    text(field('age_group'))
  ],
  NCFELD: ({ field, standardText }) => [
    heading(field('cluster')),
    text(field('standard_group')),
    htmlParagraph(standardText),
    text(field('age_range_title'))
  ],
  MDELS: ({ field, has, standardText }) => [
    heading(field('strand')),
    text(field('standard_group')),
    has('standard_num') ? preLine(`${field('standard_num')}. ${standardText}`) : htmlParagraph(standardText)
  ],
  HCPS: ({ field, standardText }) => [heading(`${field('strand_domain')}: ${field('standard_group')}`), htmlParagraph(standardText)],
  OLS: ({ standard, field, has, standardText }) => [
    heading(has('math_hs_subject') ? `(${field('math_hs_subject')}) ${field('cluster')}` : field('cluster')),
    html(standardText),
    substandards(standard, { bulletField: 'substandard_let' })
  ],
  'OLS-E': ({ standard, field, standardText }) => [
    heading(field('cluster')),
    subheading(field('level')),
    html(standardText),
    substandards(standard, { bulletField: 'substandard_let' })
  ],
  'NY.CDOS': ({ standard, field, standardText }) => [
    heading(field('level')),
    subheading(field('cluster')),
    html(standardText),
    substandards(standard, { isFullListShown: false })
  ],
  KSECE: ({ field, standardText }) => [heading(field('strand')), text(field('cluster'), 'note'), html(standardText)],
  OELDS: ({ field, standardText }) => [
    heading(`${field('strand_domain')}: ${field('topic')}`),
    html(standardText),
    lineBreak,
    text(`${field('age_range_title')} (${field('age_range')})`)
  ],
  ...forSetCodes(['NCSC.CCC', 'USS-ELA', 'HEGSE', 'ECS', 'RISELS'], htmlOnly),
  MELG: ({ field, standardText }) => [heading(field('cluster')), html(standardText), lineBreak, text(field('age_range_title'))],
  'OLS-T': ({ field, standardText }) => [heading(`Topic ${field('topic_num')}: ${field('topic')}`), html(standardText)],
  'OLS-FL': headingThenHtml('topic'),
  'OLS-PE': headingThenHtml('umbrella_standard'),
  NYSNGLS: ({ standard, field, has, standardText }) => {
    const isLeveledMath = field('subject') === 'Math' && has('level')
    return [
      heading(isLeveledMath ? `(${field('level')}) ${field('cluster')}` : field('cluster')),
      html(standardText),
      substandards(standard, { bulletField: 'substandard_let' })
    ]
  },
  MLS: ({ standard, field, standardText }) => [
    heading(field('cluster_description')),
    html(standardText),
    substandards(standard),
    when(standard.priority_standard === 'TRUE', text('(Missouri Priority Standard)'))
  ],
  WLS: ({ standard, field, standardText }) => [
    heading(field('category')),
    html(standardText),
    substandards(standard),
    when(standard.priority === true, text('(Priority Learning Standard)'))
  ],
  GELDS: ({ standard, field, has, standardText }) => [
    heading(`${field('age_range')}: ${field('cluster')}`),
    html(standardText),
    substandards(standard),
    when(
      has('kindergarten_correlation_ref_code'),
      text(`Kindergarten Correlation: (${field('kindergarten_correlation_ref_code')})`)
    )
  ],
  AELS: ({ standard, field, standardText }) => [
    heading(`${field('topic_code')}: ${field('topic')}`),
    html(standardText),
    substandards(standard)
  ],
  ...forSetCodes(['AZ.SEL', 'LSS-SS', 'MDE-SEL', 'LA.PE', 'FL.PE', 'SD.CCC', 'GA.SCI'], htmlOnly),
  ...forSetCodes(['LSS.LC', 'NACS-PE', 'IELG', 'AELG', 'AAAS'], headingThenHtml('strand_domain')),
  LELDS: ({ standard, field, standardText }) => [
    heading(field('strand')),
    htmlParagraph(standardText),
    substandards(standard, { olType: '1' }),
    text(`${field('age_range_title')} (${field('age_range')})`)
  ],
  VSOL: ({ standard, field, has, standardText }) => [
    when(has('math_hs_course'), heading(field('math_hs_course'))),
    html(standardText),
    substandards(standard)
  ],
  VASOL: ({ standard, field, has, standardText }) => [
    when(has('strand_description'), heading(field('strand_description'))),
    html(standardText),
    substandards(standard)
  ],
  MCF: ({ standard, field, standardText }) => [
    heading(joinPresent([field('math_hs_course'), field('math_hs_subject'), field('cluster')])),
    html(standardText),
    substandards(standard)
  ],
  MAELG: ({ field, standardText }) => [
    heading(field('cluster')),
    html(standardText),
    lineBreak,
    text(`${field('age_range_title')}: ${field('age_range')}`)
  ],
  PCS: pcsLayout,
  ...forSetCodes(['PEC', 'PAEC', 'SCCCRP'], ({ field, standardText }) => [
    heading(field('cluster_description')),
    text(field('cluster_text')),
    html(standardText)
  ]),
  SCCCR: ({ standard, field, standardText }) => [
    heading(joinPresent([field('math_hs_course'), field('cluster')])),
    text(field('standard_group')),
    html(standardText),
    substandards(standard)
  ],
  TSS: ({ standard, field, has, standardText }) => [
    heading(field('cluster')),
    when(has('math_hs_course') && has('math_hs_subject'), text(`${field('math_hs_course')}: ${field('math_hs_subject')}`)),
    html(standardText),
    substandards(standard)
  ],
  ...forSetCodes(['AKCS', 'DLM.EE'], ({ standard, field, has, standardText }) => [
    heading(field('cluster')),
    when(has('math_hs_subject'), text(field('math_hs_subject'))),
    html(standardText),
    substandards(standard)
  ]),
  INCC: headingThenHtml('course'),
  IELF: ({ field, standardText }) => [
    heading(field('strand')),
    text(field('topic')),
    htmlParagraph(standardText),
    text(field('age_range'))
  ],
  'MD-ELS': ({ field, standardText }) => [heading(field('strand')), htmlParagraph(standardText), text(field('age'))],
  ISELC: ({ field, standardText }) => [
    heading(field('standard_group')),
    htmlParagraph(standardText),
    text(`(${field('level')})`, 'indented')
  ],
  MCCRS: ({ standard, field, standardText }) => [
    heading(`${field('cluster')} ${field('math_hs_course')}`),
    html(standardText),
    substandards(standard)
  ],
  ...forSetCodes(
    ['M-CCRS', 'NC.ECS', 'OELALS', 'IDCS', 'TEKS.ELPS', 'WCPS', 'WCSE', 'NJSLS', 'KY', 'GSE', 'GECS', 'NVACS', 'MS-AAAS', 'AZ', 'LSS', 'KS', 'IAS', 'WAS'],
    headingHtmlSubstandards('cluster')
  ),
  'SC.ALT': headingThenHtml('strand'),
  WAAPF: ({ field, has, standardText }) => [
    when(has('math_hs_subject'), heading(field('math_hs_subject'))),
    subheading(field('standard_group')),
    html(standardText)
  ],
  WELDG: ({ field, has, standardText }) => [
    heading(field('topic')),
    when(has('standard_group'), text(field('standard_group'))),
    htmlParagraph(standardText),
    text(`${field('age_range_title')} ${field('age_range')}`)
  ],
  WASEL: ({ field, standardText }) => [
    heading(field('strand_domain')),
    htmlParagraph(standardText),
    text(field('age_range'), 'indented')
  ],
  ...forSetCodes(
    ['NVACSC', 'OH-SEL', 'FLB', 'OLS.SS', 'NGSS', 'LSS-S', 'MSSF', 'MAFS', 'LAFS', 'SC-ELS', 'OMS', 'IDCCC', 'MAS'],
    headingThenHtml('cluster')
  ),
  'Alternate-K-PREP': htmlOnly,
  FL: ({ standard, field, standardText }) => [
    heading(`${field('standard_group')} ${field('cluster')}`),
    html(standardText),
    substandards(standard)
  ],
  'MS-CCRS': ({ standard, field, has, standardText }) => [
    heading(`${field('math_hs_course')} ${field('cluster')}`),
    when(has('math_hs_subject'), text(field('math_hs_subject'))),
    html(standardText),
    substandards(standard)
  ],
  CAS: ({ standard, field, has, standardText }) => [
    when(has('cluster'), heading(prefixed(field('strand_domain'), field('cluster')))),
    html(standardText),
    substandards(standard)
  ],
  CELDG: ({ field, has, standardText }) => [
    heading(field('standard_group')),
    text(field('age_range')),
    htmlParagraph(standardText),
    when(has('footer'), text(field('footer')))
  ],
  'WIDA.ELD': ({ field, standardText }) => [
    heading(`${field('domain')} ${field('strand')}`),
    text(field('cluster')),
    htmlParagraph(standardText),
    text(WIDA_COPYRIGHT, 'muted')
  ],
  ECIPs: ({ field, has, standardText }) => [
    heading(field('cluster')),
    text(`${field('age_range')}: ${standardText}`),
    when(has('footer'), text(`${field('footer')}: ${field('kindergarten_correlation_ref_code')}`))
  ],
  MN: ({ standard, standardText }) => [html(standardText), substandards(standard)],
  NMCCSS: ({ standard, field, has, standardText }) => [
    heading(field('cluster')),
    when(has('standard_group'), text(field('standard_group'))),
    html(standardText),
    substandards(standard)
  ],
  NDCS: ({ standard, field, has, standardText }) => [
    heading(prefixed(field('standard_group'), field('cluster'))),
    html(standardText),
    substandards(standard),
    when(has('footer'), indentedHtmlParagraph(field('footer')))
  ],
  IC: (input) => [
    heading(mathSubjectClusterHeading(input)),
    html(input.standardText),
    substandards(input.standard),
    when(input.has('footnote'), indentedHtmlParagraph(input.field('footnote')))
  ],
  RELDS: ({ field, standardText }) => [
    heading(`By the following ages, most children will: ${field('cluster')}`),
    html(standardText)
  ],
  KELS: ({ standard, field, has, standardText }) => [
    heading(`${field('topic')}: ${field('age_range_title')}`),
    text(field('cluster')),
    html(standardText),
    substandards(standard),
    when(has('footer'), indentedHtmlParagraph(field('footer')))
  ],
  OELKS: ({ field, has, standardText }) => [
    heading(field('strand')),
    html(standardText),
    when(has('age_range'), indentedHtmlParagraph(field('age_range')))
  ],
  'MS-SEL': headingHtmlSubstandards('standard_group'),
  VELS: ({ standard, field, standardText }) => [
    heading(field('strand')),
    heading(field('substrand')),
    text(field('cluster')),
    html(standardText),
    substandards(standard)
  ],
  ...forSetCodes(['NJ.SLS', 'RICS'], headingParagraphSubstandards('cluster')),
  WIS: (input) => [
    heading(mathSubjectClusterHeading(input)),
    html(input.standardText),
    substandards(input.standard, { bulletField: 'substandard_let' })
  ],
  WMELS: ({ standard, field, standardText }) => [
    heading(`${field('strand')}: ${field('cluster')}`),
    html(standardText),
    substandards(standard)
  ],
  WISEL: ({ field, standardText }) => [
    heading(field('cluster')),
    htmlParagraph(standardText),
    text(field('age_range'), 'indented')
  ],
  AAS: ({ standard, field, has, standardText }) => [
    heading(joinPresent([has('course') ? `${field('course')}:` : '', field('cluster')])),
    html(standardText),
    substandards(standard)
  ],
  ACS: acsLayout,
  ...forSetCodes(['ALAAS', 'NYPKELS', 'MELDS'], ({ field, has, standardText }) => [
    when(has('strand'), heading(prefixed(field('math_hs_course'), field('strand')))),
    when(has('cluster'), subheading(field('cluster'))),
    html(standardText)
  ]),
  'AL.CTE': headingParagraphSubstandards('level'),
  ...forSetCodes(['HSA-Alt', 'VTAA'], ({ field, standardText }) => [
    heading(field('essence_statement')),
    htmlParagraph(standardText),
    text(field('perf_level'))
  ]),
  OEAF: (input) => [
    when(input.has('cluster'), heading(mathSubjectClusterHeading(input))),
    html(input.standardText),
    when(input.has('level'), text(`${input.field('level')}. ${input.field('level_text')}`, 'indented'))
  ],
  ...forSetCodes(['UTCS', 'SDSS'], ({ standard, field, has, standardText }) => [
    heading(field('cluster')),
    when(has('math_hs_course'), text(`${field('math_hs_course')}: ${field('math_hs_subject')}`)),
    html(standardText),
    substandards(standard)
  ]),
  ...forSetCodes(['WVCCRS', 'WVAAAS'], ({ standard, field, has, standardText }) => [
    heading(field('cluster')),
    when(has('math_hs_course'), text(field('math_hs_course'))),
    htmlParagraph(standardText),
    substandards(standard)
  ]),
  GMS: ({ field, has, standardText }) => [
    heading(field('cluster')),
    when(has('math_hs_course'), text(field('math_hs_course'))),
    htmlParagraph(standardText)
  ],
  'CAS-EEO': ({ field, standardText }) => [
    heading(prefixed(field('domain'), field('learning_expectation'))),
    htmlParagraph(standardText)
  ],
  OKAS: ({ field, has, standardText }) => {
    const coursePrefix = has('math_hs_course') ? `${field('math_hs_course')}:` : ''
    return [heading(field('cluster')), htmlParagraph(`${coursePrefix} ${standardText}`)]
  },
  SECD: ({ standard, field, standardText }) => [
    heading(field('topic')),
    htmlParagraph(standardText),
    substandards(standard),
    text(`(${field('level')})`)
  ],
  'MN-SEL': ({ field, has, standardText }) => [
    heading(field('cluster')),
    htmlParagraph(standardText),
    when(has('related_standard'), text(`Related Academic Standards: ${field('related_standard')}`))
  ],
  MELS: ({ standard, field, has, standardText }) => [
    heading(field('topic')),
    when(has('strand'), text(field('strand'))),
    htmlParagraph(standardText),
    substandards(standard),
    text(field('age_range_title'))
  ],
  FELDS: ({ field, has, standardText }) => [
    heading(has('cluster') ? `${field('strand')}: ${field('cluster')}` : field('strand')),
    htmlParagraph(field('topic')),
    htmlParagraph(standardText)
  ],
  NECCRS: ({ standard, field, has, standardText }) => [
    heading(field('strand')),
    when(has('cluster'), text(field('cluster'))),
    htmlParagraph(standardText),
    substandards(standard)
  ],
  NJELS: ({ standard, field, has, standardText }) => [
    heading(field('cluster')),
    when(has('strand'), text(field('strand'))),
    htmlParagraph(standardText),
    substandards(standard),
    when(has('footer_header'), text(`${field('footer_header')}: ${field('database_number')}`, 'indented'))
  ],
  TXELG: ({ standard, field, standardText }) => [
    heading(field('strand')),
    text(`${field('cluster')}: ${standardText}`),
    substandards(standard)
  ],
  'TEKS.PE': ({ standard, field, standardText }) => [
    heading(field('cluster')),
    text(`${field('high_school_course')}: ${standardText}`),
    substandards(standard)
  ],
  ...forSetCodes(['TEKS.SS', 'LA-ELDS'], headingHtmlSubstandards('domain', { olType: 'A' })),
  ...forSetCodes(['OH.ELDS', 'PTKLF', 'MLS.SS'], headingLineParagraph('strand', 'cluster')),
  PLSEC: ({ field, standardText }) => [
    heading(field('strand')),
    text(`${field('cluster')}: ${field('sub_cluster')}`),
    htmlParagraph(standardText),
    text(field('age'))
  ],
  AZELPS: headingThenHtmlParagraph('subject'),
  MLR: ({ standard, field, standardText }) => [
    heading(`${field('cluster_code')}: ${field('cluster')}`),
    text(field('math_hs_subject')),
    htmlParagraph(standardText),
    substandards(standard, { isFullListShown: false })
  ],
  VESOL: ({ field, standardText }) => [
    heading(`${field('domain')} ${field('strand')}`),
    htmlParagraph(standardText),
    text(field('complexity_continuum'))
  ],
  ...forSetCodes(['DCELS', 'NDES'], headingThenHtmlParagraph('cluster')),
  IELS: headingLineParagraph('cluster', 'standard_header'),
  'SC.CCR': ({ standard, field, standardText }) => [
    heading(`${field('domain')}: ${field('strand')}`),
    text(field('cluster')),
    htmlParagraph(standardText),
    substandards(standard, { isFullListShown: false })
  ],
  'NV.PKS': ({ field, standardText }) => [heading(`${field('domain')}: ${field('cluster')}`), htmlParagraph(standardText)],
  'NV.ELG': headingLineParagraph('age_range', 'cluster'),
  'ND.CS': ({ standard, field, standardText }) => [
    heading(field('level')),
    htmlParagraph(standardText),
    substandards(standard, { isFullListShown: false })
  ],
  ...forSetCodes(['NDELS', 'ASELD', 'NYS.SLS', 'NDSSCS', 'NVACS-H', 'ELDS', 'NY.PE', 'MN.PE', 'AL.SS'], headingThenHtmlParagraph('domain')),
  'ND.CTE': ({ field, has, standardText }) => [
    heading(field('cluster')),
    when(has('strand'), text(field('strand'))),
    htmlParagraph(standardText)
  ],
  ...forSetCodes(['INAS', 'NCS', 'NC.SS'], (input) => [heading(mathSubjectClusterHeading(input)), html(input.standardText)]),
  INELS: ({ field, standardText }) => [heading(field('domain')), text(field('strand'), 'note'), htmlParagraph(standardText)],
  NYSS: headingLineParagraph('strand', 'domain'),
  ...forSetCodes(
    [
      'CTELDS', 'ME-ELDS', 'NDPECS', 'NVESCRS', 'GSELA', 'IDCS.ELAL', 'TS', 'ELG', 'NECCRSEI', 'IAS-PE', 'MD-PE', 'NE-SS',
      'SDELG', 'WELS', 'DELF', 'OELG', 'ARELS', 'KYECS', 'MASM', 'NCOS', 'MSCS', 'NMELG', 'HFLS', 'HCSSS', 'MI.SCI', 'MI.SS',
      'MI.PE', 'GA.SS', 'AL.SCI', 'AL.PE', 'RI.PE'
    ],
    headingThenHtmlParagraph('strand')
  ),
  AR: ({ field, has, standardText }) => [
    heading(has('strand') ? `${field('strand')}:${field('cluster')}` : field('cluster')),
    htmlParagraph(standardText)
  ],
  'OLS.S': headingLineParagraph('domain', 'cluster'),
  'MLS.S': headingLineParagraph('domain', 'strand'),
  'TEKS-SS': ({ standard, field, has, standardText }) => [
    heading(joinPresent([has('high_school_course') ? `${field('high_school_course')}:` : '', field('domain')])),
    html(standardText),
    substandards(standard, { olType: 'A' })
  ],
  NHES: ({ field, standardText }) => [heading(`${field('subject')}: ${field('domain')}`), html(standardText)],
  SOL: ({ standard, field, has, standardText }) => [
    has('math_hs_course') ? heading(field('math_hs_course')) : when(has('strand'), heading(field('strand'))),
    html(standardText),
    substandards(standard, { olType: 'i' })
  ],
  'TEKS.SCI': headingParagraphSubstandards('strand', { olType: 'A' }),
  ...forSetCodes(['NPKS', 'NH-ELS'], ({ field, standardText }) => [subheading(field('cluster')), html(standardText)]),
  ...forSetCodes(['KSELA', 'TEKS-P', 'SC', 'NE-PE'], headingParagraphSubstandards('strand')),
  'CA-T-SEL': ({ field, standardText }) => [heading(`${field('age_range')}: ${field('strand')}`), htmlParagraph(standardText)],
  NDSCS: headingThenHtmlParagraph('descriptor'),
  ELP: ({ standardText }) => [htmlParagraph(standardText)],
  HICCS: ({ standard, field, standardText }) => [
    heading(`${field('strand_domain')}: ${field('cluster')}`),
    html(standardText),
    substandards(standard)
  ]
}

function isPresent(block: StandardBlock | undefined): block is StandardBlock {
  return block !== undefined
}

export function standardBlocks(standard: StandardDetail): StandardBlock[] {
  const layout = LAYOUT_BY_SET_CODE[standard.standard_set_code] ?? defaultLayout
  const input: LayoutInput = {
    standard,
    field: (name) => fieldText(standard, name),
    has: (name) => hasField(standard, name),
    standardText: standard.standard_text
  }
  return layout(input).filter(isPresent)
}
